/**
 * PlaythroughScriptEditor — v3 review gate.
 *
 * Presentation spec (this revision):
 *   - One sticky bottom action bar (no mirrored top bar).
 *   - Three tiers: [Delete playthrough (text link)] ... [Cancel] [Save edits] [Finalize →]
 *   - Honest labels: "Save edits" (not draft); "Delete playthrough" always.
 *   - Finalize → / Re-finalize / Generating… kept state-dependent.
 *   - Cancel: if clean → just leave; if dirty → minimal "Leave without saving?" confirm.
 *   - One isDirty flag guards ALL exits: Cancel button, in-app nav (handleNavAttempt),
 *     and browser exit (beforeunload).
 *   - Finalize success → navigate to editor hub.
 *   - Delete → navigate to editor hub.
 *
 * One editor, two kinds of play-through (Oct 20): the intro, and a section's
 * (/coordinator/clients/:id/edit/:sectionSlug/playthrough-script). The `slot`
 * — {kind: "intro"} or {kind: "section", sectionType} — changes only the API
 * base path, the page title and where "back" goes; everything else is shared.
 * A section's scenes also show where they were drawn from (citations).
 *
 * Any scene can be rewritten on its own ("Rewrite"): PE rewrites just that
 * scene and the rest stay exactly as edited. Unsaved edits are saved first.
 */

import { normalizeMediaUrl } from "../../lib/mediaUrl.js";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import ImagePicker from "../../components/coordinator/ImagePicker.jsx";
import SnapshotPanel from "../../components/coordinator/SnapshotPanel.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";
import StreamNotes from "../../components/coordinator/StreamNotes.jsx";
import BackLink from "../../components/BackLink.jsx";
import { useIsCompact } from "../../hooks/useIsCompact.js";
import AutoTextarea from "../../components/AutoTextarea.jsx";
import PlayThroughPlayer from "../PlayThroughPlayer.jsx";
import CitationChips from "../../components/coordinator/CitationChips.jsx";
import { sectionLabel as nameSection, SLUG_TO_TYPE, playthroughBase } from "../../lib/sections.js";

// Narration pace, as PE will synthesize it (finalize envelope `voice.pace`).
export const PACES = [
  ["very_slow", "Very slow"],
  ["slow",      "Slow"],
  ["normal",    "Normal"],
  ["fast",      "Fast"],
  ["very_fast", "Very fast"],
];
// (useBlocker unavailable without a data router; all exits go via handleNavAttempt)

// ── Leave confirm modal (minimal — §5) ────────────────────────────────────────
// "Leave without saving? [Leave] [Keep editing]"

function LeaveModal({ onLeave, onKeep }) {
  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onKeep()}>
      <div style={m.box}>
        <p style={m.msg}>You have unsaved edits. Leave without saving?</p>
        <div style={m.row}>
          <button style={m.leaveBtn}  onClick={onLeave}>Leave</button>
          <button style={m.keepBtn}   onClick={onKeep}>Keep editing</button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirm modal ───────────────────────────────────────────────────────

function DeleteModal({ clientName, isFinalized, snapshotCount, onConfirm, onCancel }) {
  const [typed, setTyped] = useState("");
  const confirmed = typed.trim().toLowerCase() === clientName.toLowerCase();
  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={m.box}>
        <h3 style={{ ...m.title, color: "var(--color-critical)" }}>Delete stream?</h3>
        <p style={m.body}>
          {isFinalized
            ? "This permanently deletes the finalized stream, including its audio and spec. The DSP view will show no stream until you generate a new one."
            : "This discards the unsaved draft. You can generate a new stream at any time."}
        </p>
        {snapshotCount > 0 && (
          <p style={m.warn}>
            This also permanently deletes {snapshotCount === 1
              ? "the 1 saved snapshot"
              : `all ${snapshotCount} saved snapshots`} for this stream. You won't be able to restore them afterward.
          </p>
        )}
        <label style={m.label}>
          Type <strong>{clientName}</strong> to confirm
          <input
            style={m.input}
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder={clientName}
            onKeyDown={e => e.key === "Enter" && confirmed && onConfirm()}
          />
        </label>
        <div style={m.row}>
          <button
            style={{ ...m.deleteBtn, ...(!confirmed ? m.disabled : {}) }}
            onClick={onConfirm}
            disabled={!confirmed}
          >Delete stream</button>
          <button style={m.keepBtn} onClick={onCancel}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Save-first gate modal (Finalize / Preview with unsaved edits) ──────────────

function SaveGateModal({ kind, onSave, onWithout, onCancel }) {
  const verb = kind === "preview" ? "preview" : "finalize";
  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={m.box}>
        <h3 style={m.title}>Save edits before {verb}?</h3>
        <p style={m.body}>
          You have unsaved edits. {kind === "preview" ? "Preview" : "Finalize"} uses your
          saved content, so unsaved edits won't be included unless you save them first.
        </p>
        <div style={{ ...m.row, flexDirection: "column", gap: "var(--space-2)" }}>
          <button style={m.saveGatePrimary} onClick={onSave}>
            Save &amp; {verb}
          </button>
          <button style={m.saveGateSecondary} onClick={onWithout}>
            {kind === "preview" ? "Preview" : "Finalize"} without them
          </button>
          <button style={m.saveGateGhost} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Scene card ─────────────────────────────────────────────────────────────────

// The grid shows the 200 px thumbnail rendition, not the 1600 px display
// image the player uses (Oct 11): ~10 KB a picture instead of a few hundred,
// which is most of why the page felt slow. The player still gets `display`.
function thumbUrl(url) {
  const u = normalizeMediaUrl(url);
  if (!u) return u;
  if (/[?&]v=/.test(u)) return u.replace(/([?&])v=[^&]*/, "$1v=thumb");
  return u + (u.includes("?") ? "&" : "?") + "v=thumb";
}

// ── Why a photo is here (v3.6) ─────────────────────────────────────────────────
// PE says why it chose each photo: `person:<id>`, `tag:<tag>`, `spread`,
// `repeat`, or `coordinator`. Photos added in this editor are marked
// "coordinator" too, so a re-author keeps them (current_images).
// Wording is a first draft for David.

export function nameOfPeople(people) {
  const firsts = {};
  (people || []).forEach(p => { const f = p.first_name || p.display_name; firsts[f] = (firsts[f] || 0) + 1; });
  const map = {};
  (people || []).forEach(p => {
    const f = p.first_name || p.display_name;
    map[p.id] = firsts[f] > 1 && p.relationship ? `${f} (${p.relationship})` : f;
  });
  return id => map[id];
}

export function becauseText(because, nameOf = () => undefined) {
  const list = Array.isArray(because) ? because : (because ? [because] : []);
  const words = list.map(b => {
    if (typeof b !== "string") return null;
    if (b === "coordinator") return "You added";
    if (b === "spread")      return "Even spread";
    if (b === "repeat")      return "Repeat";
    if (b.startsWith("person:")) return nameOf(b.slice(7)) || "A person";
    if (b.startsWith("tag:"))    return b.slice(4);
    return null;
  }).filter(Boolean);
  return words.length ? [...new Set(words)].join(", ") : null;
}

// ── Text cards (PE v3.7, Oct 27; free text Oct 28) ─────────────────────────────
// Where no photo fits a line, PE shows a short phrase drawn from it. The
// coordinator can rewrite a card freely (CG then marks it `edited`, so PE keeps
// it as written) and remove the dimmed background photo. Wording is a first
// draft for David.

export const MAX_CARD_CHARS = 120;     // a card is big type on screen; short reads best

const norm = t => (t || "").replace(/\s+/g, " ").trim().toLowerCase();

/** After a narration edit (PE v3.7 §2, mirrored so the editor can say what
 *  will happen): a card follows its own clause (`clause_text`); if that clause
 *  changed, an edited card stays on its line as written, and any other card
 *  is marked — PE resets it at finalize. */
export function remapCards(cards, clauses) {
  const last = Math.max(0, clauses.length - 1);
  return (cards || []).map(c => {
    const { stale, ...card } = c;                                   // eslint-disable-line no-unused-vars
    if (card.clause_text == null) return { ...card, clause_index: Math.min(card.clause_index ?? 0, last) };
    const want = norm(card.clause_text);
    if (norm(clauses[card.clause_index]) === want) return card;
    const k = clauses.findIndex(cl => norm(cl) === want);
    if (k >= 0) return { ...card, clause_index: k };
    const at = Math.min(card.clause_index ?? 0, last);
    return card.edited ? { ...card, clause_index: at } : { ...card, clause_index: at, stale: true };
  });
}

/** A card with the coordinator's own words. */
export function editCard(card, text) {
  return { ...card, text, edited: true, shortened: undefined, stale: undefined };
}

// Per-scene visual mode (PE v3.7 §10, Oct 28): a coordinator can re-author one
// scene as photos only or text cards only, instead of PE's automatic choice.
export const VISUAL_MODES = [
  { value: "auto",   label: "Photos and cards" },
  { value: "photos", label: "Photos only" },
  { value: "cards",  label: "Text cards only" },
];
export const modeOf = scene => (scene?.visual_mode === "photos" || scene?.visual_mode === "cards") ? scene.visual_mode : "auto";
export const modeLabel = m => (VISUAL_MODES.find(x => x.value === m) || VISUAL_MODES[0]).label;
export const canChooseMode = (scene, textCards) => !!textCards && !["hero", "sign_off"].includes(scene?.scene_id);

const TREATMENT_LABEL = {
  photo_led: "Mostly photos",
  text_led:  "Mostly text cards",
  text_only: "Text cards only",
};

function CardTextEditor({ card, clause, onSave, onCancel }) {
  const [text, setText] = useState(card.text || "");
  const clean = text.replace(/\s+/g, " ").trim();
  const ok = clean.length > 0 && clean.length <= MAX_CARD_CHARS;
  return (
    <div style={cs.picker}>
      {clause && <p style={cs.pickerHint}>From the line: “{clause}”</p>}
      <input autoFocus aria-label="Card text" value={text} maxLength={MAX_CARD_CHARS + 40}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && ok) onSave(clean); if (e.key === "Escape") onCancel(); }}
        style={cs.input} />
      <div style={cs.editRow}>
        <span style={{ ...cs.note, ...(clean.length > MAX_CARD_CHARS ? { color: "var(--color-critical)" } : {}) }}>
          {clean.length > MAX_CARD_CHARS ? `Too long — keep it under ${MAX_CARD_CHARS} characters.` : "A few words read best on screen."}
        </span>
        <button type="button" style={cs.linkBtn} onClick={onCancel}>Cancel</button>
        <button type="button" style={{ ...cs.linkBtn, ...(ok ? {} : { opacity: 0.5, cursor: "not-allowed" }) }}
          disabled={!ok} onClick={() => onSave(clean)}>Done</button>
      </div>
    </div>
  );
}

export function CardsPanel({ beats, onChange, clientId }) {
  const [editing, setEditing] = useState(null);   // card index
  if (!beats || !(beats.cards || []).length) return null;
  const clauses = beats.clauses || [];
  const ambientUrl = beats.ambient?.media_id
    ? `/api/clients/${clientId}/media/${encodeURIComponent(beats.ambient.media_id)}?v=thumb` : null;
  return (
    <div style={cs.panel}>
      <div style={cs.head}>
        <p style={cs.label}>Text cards</p>
        {beats.treatment && <span style={cs.badge}>{TREATMENT_LABEL[beats.treatment] || beats.treatment}</span>}
      </div>
      <p style={cs.note}>Shown where no photo fits what's being said.</p>
      <ul style={cs.list}>
        {beats.cards.map((c, i) => (
          <li key={i} style={cs.item}>
            <span style={cs.cardText}>“{c.text}”</span>
            {c.edited && <span style={cs.flag}>your words</span>}
            {c.shortened && !c.edited && <span style={cs.flag}>shortened</span>}
            {c.stale && <span style={cs.flagWarn}>its line changed — will reset unless you edit it</span>}
            {editing !== i && (
              <button type="button" style={cs.linkBtn} onClick={() => setEditing(i)}>Edit</button>
            )}
            {editing === i && (
              <CardTextEditor card={c} clause={clauses[c.clause_index]}
                onCancel={() => setEditing(null)}
                onSave={text => {
                  const cards = beats.cards.map((x, k) => (k === i ? editCard(x, text) : x));
                  onChange({ ...beats, cards });
                  setEditing(null);
                }} />
            )}
          </li>
        ))}
      </ul>
      {beats.ambient?.media_id ? (
        <div style={cs.ambientRow}>
          {ambientUrl && <img src={ambientUrl} alt="" style={cs.ambientThumb} />}
          <span style={cs.note}>Dimmed background photo behind the cards.</span>
          <button type="button" style={cs.linkBtn} onClick={() => onChange({ ...beats, ambient: null })}>Remove</button>
        </div>
      ) : (
        <p style={cs.note}>No background photo; cards show on a plain background.</p>
      )}
    </div>
  );
}

const cs = {
  panel:   { borderTop: "1px solid var(--color-border)", paddingTop: "var(--space-3)", display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  head:    { display: "flex", alignItems: "center", gap: "var(--space-2)" },
  label:   { margin: 0, fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" },
  badge:   { fontSize: 11, color: "var(--color-text-secondary)", border: "1px solid var(--color-border)", borderRadius: 999, padding: "1px 8px" },
  note:    { margin: 0, fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  list:    { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  item:    { display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "var(--space-2)", fontSize: "var(--text-sm)" },
  cardText:{ fontFamily: "var(--font-serif)", color: "var(--color-text-primary)" },
  flag:    { fontSize: 11, color: "var(--color-text-tertiary)" },
  flagWarn:{ fontSize: 11, color: "var(--color-critical)" },
  linkBtn: { background: "none", border: "none", padding: 0, fontSize: "var(--text-xs)", color: "var(--color-accent)", cursor: "pointer" },
  picker:  { flexBasis: "100%", background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-3)", display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  pickerHint: { margin: 0, fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" },
  input:   { font: "inherit", fontFamily: "var(--font-serif)", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-1) var(--space-2)", background: "var(--color-bg-base)", color: "var(--color-text-primary)" },
  editRow: { display: "flex", alignItems: "center", gap: "var(--space-3)" },
  ambientRow: { display: "flex", alignItems: "center", gap: "var(--space-2)" },
  ambientThumb: { width: 40, height: 40, objectFit: "cover", borderRadius: "var(--radius-sm)", opacity: 0.6, filter: "blur(1px)" },
};

/** The scene's card plan as the editor tracks it. */
export function beatsOfScene(s) {
  if (!Array.isArray(s?.cards) || !s.cards.length) return undefined;
  return { cards: s.cards, clauses: s.clauses || [], ambient: s.ambient || null, treatment: s.treatment || null };
}

/** The scene with the editor's card plan written back. */
export function withBeats(s, beats) {
  if (!beats) return s;
  const out = { ...s, cards: beats.cards, clauses: beats.clauses };
  if (beats.ambient?.media_id) out.ambient = { media_id: beats.ambient.media_id };
  else delete out.ambient;
  out.cards = (beats.cards || []).map(({ stale, ...c }) =>            // eslint-disable-line no-unused-vars
    Object.fromEntries(Object.entries(c).filter(([, v]) => v !== undefined)));
  return out;
}

function SceneCard({ scene, narration, onChange, title, onTitleChange, onScreenText, onOnScreenTextChange, images, onImagesChange, onAddImages, onPreviewScene, isPreviewing, previewSceneId, onRewrite, rewriting, rewriteDisabled, citations, nameOf, beats, onBeatsChange, clientId, textCards }) {
  const compact = useIsCompact();
  const isNarrationEdited    = narration !== (scene.narration_text || "");
  const isTitleEdited        = title !== (scene.title || "");
  const isOnScreenTextEdited = onScreenText !== (scene.on_screen_text || "");
  const isImagesEdited       = JSON.stringify(images) !== JSON.stringify(scene.pending_images || []);
  const isEdited             = isNarrationEdited || isTitleEdited || isOnScreenTextEdited || isImagesEdited;

  function moveImage(i, dir) {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onImagesChange(next);
  }

  function removeImage(i) {
    onImagesChange(images.filter((_, idx) => idx !== i));
  }

  // Drag state: which thumbnail is being carried, which one it is over.
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  function dropImage(from, to) {
    setDragFrom(null); setDragOver(null);
    if (!Number.isInteger(from) || from === to || from < 0 || from >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onImagesChange(next);
  }

  return (
    <div style={{ ...sc.card, ...(compact ? sc.cardCompact : {}) }}>
      <div style={sc.header}>
        <input
          style={{ ...sc.titleInput, ...(isTitleEdited ? sc.titleInputEdited : {}) }}
          value={title}
          onChange={e => onTitleChange(e.target.value)}
          placeholder={scene.scene_id}
          title="Scene heading — shown at the start of this scene in the stream"
          maxLength={80}
        />
        <span style={sc.sceneId}>{scene.scene_id}</span>
        {isEdited && <span style={sc.editedBadge}>edited</span>}
        {onRewrite && canChooseMode(scene, textCards) && (
          <select aria-label="Show this scene as" title="How this scene is shown: re-authors the scene when changed"
            value={modeOf(scene)} disabled={rewriteDisabled}
            style={{ ...sc.modeSelect, marginLeft: "auto" }}
            onChange={e => onRewrite(scene.scene_id, e.target.value)}>
            {VISUAL_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        )}
        {onRewrite && (
          <button
            style={{ ...sc.rewriteBtn, ...(rewriteDisabled ? sc.reauthorBtnDisabled : {}),
                     ...(canChooseMode(scene, textCards) ? {} : { marginLeft: "auto" }) }}
            onClick={() => onRewrite(scene.scene_id)}
            disabled={rewriteDisabled}
            title="Ask for a fresh version of just this scene — the other scenes stay as they are"
          >{rewriting ? "Re-authoring…" : "Re-author"}</button>
        )}
        <button
          style={{ ...sc.previewBtn, ...(isPreviewing ? sc.reauthorBtnDisabled : {}), ...(onRewrite ? {} : { marginLeft: "auto" }) }}
          onClick={() => onPreviewScene(scene.scene_id)}
          disabled={isPreviewing}
          title="Finalize a preview of this scene without affecting the live stream"
        >{isPreviewing && previewSceneId === scene.scene_id ? "Previewing…" : "Finalize preview"}</button>
      </div>
      <div style={{ ...sc.body, ...(compact ? sc.bodyCompact : {}) }}>
        <div style={sc.contextCol}>
          <div style={sc.block}>
            <div style={sc.imgLabelRow}>
              <p style={sc.label}>Images ({images.length})</p>
              <button style={sc.addImgBtn} onClick={onAddImages} title="Add images from library">+ Add</button>
            </div>
            {images.length === 0 && (
              <p style={sc.noImgs}>No images — click Add to select from the library.</p>
            )}
            {/* Drag to reorder (Oct 11): the thumbnail is the handle. Keyboard:
                focus a thumbnail, ← → moves it. The order number sits on the
                image so a reorder reads at a glance. */}
            <div style={{ ...sc.thumbGrid, ...(compact ? sc.thumbGridCompact : {}) }} role="list" aria-label="Scene images, drag to reorder">
              {images.map((img, i) => (
                <div key={img.media_id || i} role="listitem" tabIndex={0}
                  draggable
                  aria-label={`Image ${i + 1} of ${images.length}${img.alt_text ? `, ${img.alt_text}` : ""}. Drag, or use arrow keys, to reorder.`}
                  onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); setDragFrom(i); }}
                  onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOver !== i) setDragOver(i); }}
                  onDragLeave={() => { if (dragOver === i) setDragOver(null); }}
                  onDrop={e => { e.preventDefault(); const from = dragFrom ?? Number(e.dataTransfer.getData("text/plain")); dropImage(from, i); }}
                  onKeyDown={e => {
                    if (e.key === "ArrowLeft" || e.key === "ArrowUp")  { e.preventDefault(); moveImage(i, -1); }
                    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); moveImage(i, 1); }
                    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeImage(i); }
                  }}
                  style={{ ...sc.thumbWrap,
                           ...(dragFrom === i ? sc.thumbDragging : {}),
                           ...(dragOver === i && dragFrom !== null && dragFrom !== i ? (dragFrom < i ? sc.thumbOverAfter : sc.thumbOverBefore) : {}) }}>
                  <img src={thumbUrl(img.asset_url ?? img.url)} alt={img.alt_text || ""} loading="lazy"
                    style={sc.thumb} draggable={false} onError={e => { e.target.style.visibility = "hidden"; }} />
                  <span style={sc.thumbIndex} aria-hidden="true">{i + 1}</span>
                  {becauseText(img.because, nameOf) && (
                    <span style={sc.thumbWhy} title={`Why it's here: ${becauseText(img.because, nameOf)}`}>
                      {becauseText(img.because, nameOf)}
                    </span>
                  )}
                  <button type="button" style={sc.thumbRemove} onClick={() => removeImage(i)} aria-label={`Remove image ${i + 1}`} title="Remove">×</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={sc.narrationCol}>
          <p style={sc.label}>Narration <span style={sc.labelNote}>(editable)</span></p>
          <AutoTextarea
            style={{ ...sc.textarea, ...(isNarrationEdited ? sc.textareaEdited : {}) }}
            value={narration}
            onChange={e => onChange(e.target.value)}
            rows={3}
            placeholder="Spoken narration…"
          />
          <p style={sc.label}>On-screen text <span style={sc.labelNote}>(editable)</span></p>
          <input
            type="text"
            style={{ ...sc.onScreenInput, ...(isOnScreenTextEdited ? sc.textareaEdited : {}) }}
            value={onScreenText}
            onChange={e => onOnScreenTextChange(e.target.value)}
            placeholder="Text shown on screen…"
          />
        </div>
      </div>
      <CardsPanel beats={beats} onChange={onBeatsChange} clientId={clientId} />
      {citations}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function PlaythroughScriptEditor({ slot: slotProp = null } = {}) {
  const { clientId, sectionSlug } = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const compact      = useIsCompact();

  // Intro or a section (Oct 20) — from the prop, else from the route.
  const sectionType  = slotProp?.kind === "section" ? slotProp.sectionType
                     : slotProp?.kind === "intro"   ? null
                     : (SLUG_TO_TYPE[sectionSlug] || null);
  // A section route with a slug that names no section must not fall back to
  // the intro's play-through (its Delete would delete the intro's).
  const unknownSection = !slotProp && sectionSlug && !SLUG_TO_TYPE[sectionSlug];
  const isSection    = !!sectionType;
  const apiBase      = playthroughBase(clientId, sectionType);

  const editorHubPath = isSection
    ? `/coordinator/clients/${clientId}/edit/${sectionSlug || ""}`
    : `/coordinator/clients/${clientId}/edit`;

  // Server state
  const [bundle,      setBundle]     = useState(null);
  const [ptStatus,    setPtStatus]   = useState(null);
  const [clientName,  setClientName] = useState("");
  const [clientPronouns, setClientPronouns] = useState("");
  const [people, setPeople] = useState([]);   // to name `person:<id>` reasons
  const sectionLabel = isSection ? nameSection(sectionType, clientPronouns) : null;
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState(null);

  // Local draft tracking
  const [savedNarrations,  setSavedNarrations]  = useState({});
  const [localNarrations,  setLocalNarrations]  = useState({});
  const [savedOnScreenText, setSavedOnScreenText] = useState({});  // { scene_id: string }
  const [localOnScreenText, setLocalOnScreenText] = useState({});  // parallel to localNarrations
  const [savedTitles, setSavedTitles] = useState({});  // { scene_id: string } — scene headings
  const [localTitles, setLocalTitles] = useState({});  // parallel to localNarrations
  const [savedImages,      setSavedImages]      = useState({});  // { scene_id: pending_images[] }
  const [localImages,      setLocalImages]      = useState({});  // parallel to localNarrations
  const [savedBeats,       setSavedBeats]       = useState({});  // { scene_id: {cards, clauses, ambient, treatment} } (v3.7)
  const [localBeats,       setLocalBeats]       = useState({});

  // ImagePicker modal
  const [pickerSceneId, setPickerSceneId] = useState(null);

  // Action state
  const [saving,     setSaving]     = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [deleting,   setDeleting]   = useState(false);

  // Modal state
  const [showDelete, setShowDelete] = useState(false);

  const snapshotPanelRef = React.useRef(null);  // exposes captureSnapshot() for the Snapshots panel

  // Preview finalize state
  const [isPreviewing,    setIsPreviewing]    = useState(false);
  const [previewSceneId,  setPreviewSceneId]  = useState(null);  // scene ID whose preview was last requested

  // ── Single authoritative dirty flag ──────────────────────────────────────
  // Covers narration edits AND image add/delete/reorder (CG Planner Note 1).
  // All exit paths (Cancel, handleNavAttempt, beforeunload) consult this one flag.
  const isDirty = Boolean(bundle) && (
    Object.keys(localNarrations).some(id => localNarrations[id] !== (savedNarrations[id] ?? "")) ||
    Object.keys(localOnScreenText).some(id => localOnScreenText[id] !== (savedOnScreenText[id] ?? "")) ||
    Object.keys(localTitles).some(id => localTitles[id] !== (savedTitles[id] ?? "")) ||
    Object.keys(localImages).some(id =>
      JSON.stringify(localImages[id]) !== JSON.stringify(savedImages[id] ?? [])
    ) ||
    Object.keys(localBeats).some(id => JSON.stringify(localBeats[id]) !== JSON.stringify(savedBeats[id]))
  );

  // Clear "Edits saved" label as soon as the user makes a new edit
  useEffect(() => { if (isDirty) setSavedFlash(false); }, [isDirty]);

  // ── In-app leave guard (§6a) ─────────────────────────────────────────────
  // useBlocker requires a data router; app uses BrowserRouter, so we use a
  // manual "attempted nav" pattern instead. All navigation in this component
  // goes through handleNavAttempt(), which checks isDirty before proceeding.
  const [attemptedNav, setAttemptedNav] = useState(null);
  const showLeaveConfirm = attemptedNav !== null;

  function handleNavAttempt(path) {
    if (isDirty) { setAttemptedNav(path); }
    else { navigate(path); }
  }

  // ── Browser-level exit guard (§6b) — beforeunload ─────────────────────────
  useEffect(() => {
    function onBeforeUnload(e) {
      if (isDirty) { e.preventDefault(); e.returnValue = ""; }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadAll = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      // People only name the photo reasons; a failure here costs nothing else.
      fetch(`/api/clients/${clientId}/people`).then(r => (r.ok ? r.json() : []))
        .then(d => setPeople(Array.isArray(d) ? d : [])).catch(() => {});
      const [bundleRes, statusRes, clientRes] = await Promise.all([
        fetch(`${apiBase}/draft`),
        fetch(apiBase),
        fetch(`/api/clients/${clientId}`),
      ]);
      if (bundleRes.ok) {
        const b = await bundleRes.json();
        setBundle(b);
        const init = {};
        const onScreenInit = {};
        const titleInit = {};
        const imgInit = {};
        const beatInit = {};
        (b.scenes || []).forEach(s => {
          const bt = beatsOfScene(s);
          if (bt) beatInit[s.scene_id] = bt;
          init[s.scene_id]         = s.narration_text || "";
          onScreenInit[s.scene_id] = s.on_screen_text || "";
          titleInit[s.scene_id]    = s.title || "";
          imgInit[s.scene_id]      = s.pending_images; // undefined if absent — preserves "PE selects" signal
        });
        setSavedNarrations(init);
        setLocalNarrations(init);
        setSavedOnScreenText(onScreenInit);
        setLocalOnScreenText(onScreenInit);
        setSavedTitles(titleInit);
        setLocalTitles(titleInit);
        setSavedImages(imgInit);
        setLocalImages(imgInit);
        setSavedBeats(beatInit);
        setLocalBeats(beatInit);
      }
      if (statusRes.ok) setPtStatus(await statusRes.json());
      if (clientRes.ok) { const d = await clientRes.json(); setClientName(d.display_name || ""); setClientPronouns(d.pronouns || ""); }
    } catch (e) {
      setError("Failed to load: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, apiBase]);

  useEffect(() => { if (!unknownSection) loadAll(); }, [loadAll, unknownSection]);

  // ── Poll while generating (safety net) ────────────────────────────────────
  // Re-author was removed from this page, so generation isn't triggered here.
  // This remains only as a safety net: if the page is ever loaded while a
  // generation is in flight, refresh the bundle once it completes.
  useEffect(() => {
    if (ptStatus?.status !== "generating") return;
    const id = setInterval(async () => {
      const res = await fetch(apiBase);
      if (!res.ok) return;
      const s = await res.json();
      if (s.status === "review_pending") {
        clearInterval(id);
        await loadAll();
        return;
      }
      if (s.status !== "generating") {
        clearInterval(id);
        setPtStatus(s);
      }
    }, 2000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptStatus?.status, clientId]);

  // ── Poll while previewing (preview_available doesn't change status) ────────
  useEffect(() => {
    if (!isPreviewing) return;
    const id = setInterval(async () => {
      const res = await fetch(apiBase);
      if (!res.ok) return;
      const s = await res.json();
      if (s.preview_available) {
        setIsPreviewing(false);
        clearInterval(id);
        setPtStatus(s);   // update with preview_available + preview_scene_ids
        setShowPreview(true);   // and play it here, over the editor (Oct 11)
      } else if (s.error && s.error !== ptStatus?.error) {
        // Preview failed — surface the error
        setIsPreviewing(false);
        clearInterval(id);
        setPtStatus(s);
      }
    }, 3000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewing, clientId]);

  // ── Poll while finalizing ─────────────────────────────────────────────────

  useEffect(() => {
    if (ptStatus?.status !== "finalizing") return;
    const id = setInterval(async () => {
      const res = await fetch(apiBase);
      if (!res.ok) return;
      const s = await res.json();
      setPtStatus(s);
      if (s.status !== "finalizing") {
        clearInterval(id);
        setFinalizing(false);
        if (s.status === "ready") {
          // Finalize success → editor hub. Finalize used the saved bundle, so we
          // do NOT promote unsaved local edits to "saved" here (that would
          // silently bless edits the user may have chosen via "Finalize without
          // them"). React Router navigation doesn't trip beforeunload, and this
          // programmatic nav bypasses the in-app leave guard, so no fake-save is
          // needed. Any unsaved edits remain unsaved (and were the user's choice).
          setAttemptedNav(null);
          navigate(editorHubPath, { replace: true });
        }
      }
    }, 3000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptStatus?.status, clientId]);

  // ── Poll while rewriting a scene ──────────────────────────────────────────
  // When PE is done the server has merged the new scene into the draft; reload
  // quietly (no full-page spinner). Editing is locked meanwhile, so there are
  // no local edits for the reload to lose.
  useEffect(() => {
    if (!ptStatus?.rewriting) return;
    const id = setInterval(async () => {
      const res = await fetch(apiBase);
      if (!res.ok) return;
      const s = await res.json();
      if (!s.rewriting) {
        clearInterval(id);
        await loadAll({ quiet: true });
        if (s.error) setError(s.error);
      }
    }, 2500);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptStatus?.rewriting, apiBase]);

  // ── Build reviewed bundle from local edits ────────────────────────────────

  function buildReviewedBundle() {
    if (!bundle) return null;
    return {
      ...bundle,
      scenes: (bundle.scenes || []).map(s => withBeats({
        ...s,
        narration_text: localNarrations[s.scene_id]  ?? s.narration_text,
        on_screen_text: localOnScreenText[s.scene_id] ?? s.on_screen_text,
        title:          localTitles[s.scene_id]      ?? s.title,
        pending_images: localImages[s.scene_id]      ?? s.pending_images ?? [],
      }, localBeats[s.scene_id])),
    };
  }

  // Build the bundle from SAVED state only (server bundle overlaid with the
  // last-persisted saved* maps), ignoring any unsaved local edits. Finalize and
  // preview use this so that what you finalize/preview is exactly what you saved
  // — never stale-vs-unsaved ambiguity.
  function buildSavedBundle() {
    if (!bundle) return null;
    return {
      ...bundle,
      scenes: (bundle.scenes || []).map(s => withBeats({
        ...s,
        narration_text: savedNarrations[s.scene_id]  ?? s.narration_text,
        on_screen_text: savedOnScreenText[s.scene_id] ?? s.on_screen_text,
        title:          savedTitles[s.scene_id]      ?? s.title,
        pending_images: savedImages[s.scene_id]      ?? s.pending_images ?? [],
      }, savedBeats[s.scene_id])),
    };
  }

  // Persist current local edits to the server draft. Returns the persisted
  // bundle on success (so callers can use it immediately without waiting for
  // the async saved* state updates to flush), or null on failure.
  async function persistDraft() {
    // v3.7: a card scene whose narration changed gets PE's new clause split,
    // and its cards follow their words. If the split can't be had, the old
    // clauses stay and PE resets any card whose words are gone at finalize.
    let beatsNow = localBeats;
    for (const id of Object.keys(localBeats)) {
      if ((localNarrations[id] ?? "") === (savedNarrations[id] ?? "")) continue;
      try {
        const r = await fetch(`/api/clients/${clientId}/playthrough/clauses`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: localNarrations[id] }),
        });
        if (!r.ok) continue;
        const { clauses } = await r.json();
        const bt = beatsNow[id];
        beatsNow = { ...beatsNow, [id]: { ...bt, clauses, cards: remapCards(bt.cards, clauses) } };
      } catch { /* keep the old clauses */ }
    }
    if (beatsNow !== localBeats) setLocalBeats(beatsNow);
    const reviewed = (() => {
      const b = buildReviewedBundle();
      if (!b) return null;
      return { ...b, scenes: b.scenes.map(sc => (beatsNow[sc.scene_id] ? withBeats(sc, beatsNow[sc.scene_id]) : sc)) };
    })();
    if (!reviewed) return null;
    const res = await fetch(`${apiBase}/draft`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reviewed),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error || "Save failed"); return null; }
    // Mark everything saved (local becomes the new saved baseline).
    setSavedNarrations({ ...localNarrations });
    setSavedOnScreenText({ ...localOnScreenText });
    setSavedTitles({ ...localTitles });
    setSavedImages({ ...localImages });
    setSavedBeats({ ...beatsNow });
    setPtStatus(prev => ({ ...prev, ...data }));
    return reviewed;
  }

  // Pending "save edits first?" decision. When set, a small modal asks the user
  // to Save & continue / Continue without saving / Cancel before finalize/preview.
  // Shape: { kind: "finalize" | "preview", sceneId?: string }
  const [saveGate, setSaveGate] = useState(null);


  // ── Actions ───────────────────────────────────────────────────────────────

  async function handleSaveDraft() {
    if (!bundle) return;
    setSaving(true);
    setError(null);
    try {
      const ok = await persistDraft();
      if (ok) setSavedFlash(true);
    } catch (e) { setError("Save failed: " + e.message); }
    finally { setSaving(false); }
  }

  // Entry point from the Finalize button. If there are unsaved edits, open the
  // save gate; otherwise finalize the saved bundle directly.
  function handleFinalize() {
    if (isDirty) { setSaveGate({ kind: "finalize" }); return; }
    runFinalize();
  }

  // Finalize the SAVED bundle (ignores unsaved local edits by construction).
  // An explicit bundle may be passed (e.g. the just-persisted bundle from the
  // save gate) to avoid reading not-yet-flushed saved* state.
  async function runFinalize(explicitBundle) {
    const saved = explicitBundle || buildSavedBundle();
    if (!saved) return;
    setFinalizing(true);
    setError(null);
    try {
      const res  = await fetch(`${apiBase}/finalize`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(saved),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Finalize failed"); setFinalizing(false); return; }
      setPtStatus(prev => ({ ...prev, status: "finalizing" }));
      // Navigation happens in the polling effect on status === "ready"
    } catch (e) { setError("Finalize failed: " + e.message); setFinalizing(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      // The section endpoint requires the client's name for a play-through
      // caregivers can watch; the intro's ignores it. Typed in the modal.
      const res = await fetch(`${apiBase}?confirm=${encodeURIComponent(clientName)}`, { method: "DELETE" });
      if (!res.ok) { const d = await res.json(); setError(d.error || "Delete failed"); return; }
      setBundle(null);    // clears isDirty so no leave guard fires
      setShowDelete(false);
      navigate(editorHubPath, { replace: true });
    } catch (e) { setError("Delete failed: " + e.message); }
    finally { setDeleting(false); }
  }

  // ── Scoped preview finalize ──────────────────────────────────────────────
  // Entry from the scene Preview button. Preview renders from SAVED content so
  // that what you hear is exactly what Finalize will produce. If the scene has
  // unsaved edits, open the save gate first.
  function handlePreviewScene(sceneId) {
    if (isDirty) { setSaveGate({ kind: "preview", sceneId }); return; }
    runPreview(sceneId);
  }

  async function runPreview(sceneId, explicitBundle) {
    const saved = explicitBundle || buildSavedBundle();
    if (!saved) return;
    // Send only the requested scene to PE — single-scene preview
    const scoped = {
      ...saved,
      scenes: saved.scenes.filter(s => s.scene_id === sceneId),
    };
    setIsPreviewing(true);
    setPreviewSceneId(sceneId);
    try {
      const res = await fetch(`${apiBase}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scoped),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.error || "Could not start preview");
        setIsPreviewing(false);
      }
    } catch (e) {
      alert("Could not start preview: " + e.message);
      setIsPreviewing(false);
    }
  }

  // ── Rewrite one scene (scoped re-author) ─────────────────────────────────
  async function handleRewrite(sceneId, mode) {
    const name = (localTitles[sceneId] || sceneId);
    const asMode = mode ? ` as “${modeLabel(mode)}”` : "";
    if (!window.confirm(`Re-author “${name}”${asMode}? Its narration, on-screen text and images are replaced with a fresh version, except photos you added yourself. The other scenes stay as they are.`)) return;
    setError(null);
    if (isDirty) {
      // Rewriting reloads the script; save edits so none are lost.
      setSaving(true);
      const ok = await persistDraft().catch(e => { setError("Save failed: " + e.message); return null; });
      setSaving(false);
      if (!ok) return;
    }
    try {
      const res = await fetch(`${apiBase}/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mode ? { scene_ids: [sceneId], scene_modes: { [sceneId]: mode } } : { scene_ids: [sceneId] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || "Could not start re-authoring"); return; }
      setPtStatus(prev => ({ ...prev, rewriting: true, rewriting_scene_ids: data.scene_ids || [sceneId], error: null }));
    } catch (e) { setError("Could not start re-authoring: " + e.message); }
  }

  // ── Save gate resolution (shared by Finalize and Preview) ─────────────────
  async function resolveSaveGate(choice) {
    const gate = saveGate;
    setSaveGate(null);
    if (!gate || choice === "cancel") return;

    let persistedBundle = null;
    if (choice === "save") {
      setSaving(true);
      try {
        persistedBundle = await persistDraft();
        if (!persistedBundle) { setSaving(false); return; }  // error already surfaced
      } catch (e) { setError("Save failed: " + e.message); setSaving(false); return; }
      setSaving(false);
    }
    // choice === "save": pass the just-persisted bundle directly (saved* state
    //   hasn't flushed yet, so buildSavedBundle would read stale values).
    // choice === "without": persistedBundle stays null → run* uses the prior
    //   saved baseline via buildSavedBundle, ignoring unsaved local edits.
    if (gate.kind === "finalize")      runFinalize(persistedBundle);
    else if (gate.kind === "preview")  runPreview(gate.sceneId, persistedBundle);
  }

  // ── ImagePicker handlers ──────────────────────────────────────────────────
  // item is the full _media_dict object from ImagePicker (GET /api/clients/:id/media).
  // Build the complete pending_image shape that pe_adapter produces so PE receives
  // coordinator-added images in the same format as PE-authored ones.
  function handlePickerSelect(item) {
    if (!pickerSceneId) return;
    const newImg = {
      media_id:   item.id,
      url:        `${window.location.origin}/api/clients/${clientId}/media/${item.id}`,
      alt_text:   item.alt_text  || "",
      filename:   item.filename,
      tags:       item.tags      || [],
      dimensions: item.dimensions ?? null,
      because:    ["coordinator"],   // kept when the scene is re-authored (v3.6)
    };
    setLocalImages(prev => ({
      ...prev,
      [pickerSceneId]: [...(prev[pickerSceneId] || []), newImg],
    }));
  }

  // ── Derived display ───────────────────────────────────────────────────────

  const isFinalized    = ptStatus?.status === "ready";
  const nameOf         = nameOfPeople(people);
  // A scene rewrite leaves the status alone (what caregivers see is untouched)
  // and is reported by its own flag.
  const isRewriting    = ptStatus?.rewriting === true;
  const isFinalizing_  = ptStatus?.status === "finalizing" || finalizing || ptStatus?.status === "generating" || isRewriting;
  const isGenerating   = ptStatus?.status === "generating";
  const previewAvailable = ptStatus?.preview_available === true;
  // The preview plays in an overlay over this page — the same stage the
  // caregiver sees — and closes back to the editor. It used to open a second
  // tab, which left two copies of the app open (David, Oct 11).
  const [showPreview, setShowPreview] = useState(false);
  const editedSinceFin = ptStatus?.edited_since_finalized;
  const finalizeLabel  = isRewriting ? "Re-authoring…" : isFinalizing_ ? "Generating…" : isFinalized ? "Re-finalize" : "Finalize →";
  const pageTrail      = [{ label: isSection ? `${sectionLabel} stream` : "Introduction stream" }];
  // Which plan scenes the coordinator added — their scenes read "from your note".
  const originOf       = Object.fromEntries(((ptStatus?.plan?.scenes) || []).map(p => [p.scene_id, p.origin]));
  const droppedScenes  = ptStatus?.dropped_scenes || [];

  // ── Render ────────────────────────────────────────────────────────────────

  if (unknownSection) return (
    <div style={s.page}><div style={s.content}><p style={s.hint}>Unknown section: {sectionSlug}</p></div></div>
  );

  if (loading) return (
    <div style={s.page}><div style={s.content}><p style={s.hint}>Loading…</p></div></div>
  );

  if (!bundle) return (
    <div style={s.page}>
      <div style={{ ...s.content, ...(compact ? s.contentCompact : {}) }}>
        <ClientBreadcrumb
          clientName={clientName}
          role={user?.role}
          clientHref={editorHubPath}
          trail={pageTrail}
          onNavigate={handleNavAttempt}
        />
        <div style={s.empty}>
          <p style={{ fontSize: 40 }}>📋</p>
          <p style={s.emptyTitle}>No draft available</p>
          <p style={s.hint}>Generate a stream first.</p>
          <BackLink to={editorHubPath} onNavigate={handleNavAttempt} />
        </div>
      </div>
    </div>
  );

  return (
    <div style={s.page}>

      {/* Scene preview, in place: the stage over the editor; × or the end
          card's button closes it and the editor is exactly as you left it. */}
      {showPreview && (
        <PlayThroughPlayer key="preview" embedded preview autoplay clientId={clientId}
                           playthroughBase={apiBase}
                           onExit={() => setShowPreview(false)} />
      )}

      {/* Leave confirm — fired by any guarded navigation while dirty */}
      {showLeaveConfirm && (
        <LeaveModal
          onLeave={() => { const p = attemptedNav; setAttemptedNav(null); navigate(p); }}
          onKeep={() => setAttemptedNav(null)}
        />
      )}

      {/* Delete confirm */}
      {showDelete && (
        <DeleteModal
          clientName={clientName}
          isFinalized={isFinalized}
          snapshotCount={ptStatus?.snapshot_count || 0}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {/* Save-first gate (Finalize / Preview when there are unsaved edits) */}
      {saveGate && (
        <SaveGateModal
          kind={saveGate.kind}
          onSave={() => resolveSaveGate("save")}
          onWithout={() => resolveSaveGate("without")}
          onCancel={() => resolveSaveGate("cancel")}
        />
      )}

      <div style={{ ...s.content, ...(compact ? s.contentCompact : {}) }}>
        <ClientBreadcrumb
          clientName={clientName}
          role={user?.role}
          clientHref={editorHubPath}
          trail={pageTrail}
          onNavigate={handleNavAttempt}
        />

        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.title}>{isSection ? `Review the “${sectionLabel}” stream` : "Review the introduction stream"}</h1>
            <div style={s.statusRow}>
              {isFinalized && !editedSinceFin && !isFinalizing_ && <span style={{ ...s.badge, ...s.badgeFinalized }}>Finalized</span>}
              {isFinalized && editedSinceFin  && !isFinalizing_ && <span style={{ ...s.badge, ...s.badgeEdited }}>Finalized · edits not live</span>}
              {!isFinalized && !isFinalizing_ && <span style={{ ...s.badge, ...s.badgeDraft }}>Draft</span>}
              {isFinalizing_                  && <span style={{ ...s.badge, ...s.badgeFinalizing }}>{isRewriting ? "Re-authoring…" : "Finalizing…"}</span>}
              {isDirty && <span style={s.dirtyHint}>Unsaved changes</span>}
            </div>
          </div>
        </div>

        {/* Edited-since-finalized banner — hidden while a finalize/generate is
            in flight, where the "click Re-finalize" advice would be stale. */}
        {editedSinceFin && !isFinalizing_ && (
          <div style={s.editedBanner}>
            Your recent edits aren't live yet — click <strong>Re-finalize</strong> to regenerate audio with your changes.
          </div>
        )}

        {/* Scenes the citation check left out (section play-throughs) */}
        {droppedScenes.length > 0 && (
          <div style={s.editedBanner}>
            {droppedScenes.length === 1 ? "One scene was" : `${droppedScenes.length} scenes were`} left out
            because nothing in {droppedScenes.length === 1 ? "it" : "them"} could be traced to the profile:
            {" "}{droppedScenes.map(d => d.title || d.scene_id).join(", ")}.
          </div>
        )}

        {/* PE's notes and a failed finalize (Oct 25) — were only in the log. */}
        <StreamNotes
          authorWarnings={ptStatus?.author_warnings}
          finalizeWarnings={isFinalized ? ptStatus?.finalize_warnings : null}
          error={isFinalizing_ || isRewriting || ptStatus?.error === error ? null : ptStatus?.error}
          titleOf={id => ((bundle?.scenes || []).find(sc => sc.scene_id === id)?.title) || id}
        />

        {/* Error */}
        {error && (
          <div style={s.errorBox}><p style={s.errorText}>{error}</p></div>
        )}

        {/* Subtitle */}
        <p style={s.subtitle}>
          Edit narration, images (add / remove / reorder), or both.
          {" "}<strong>Save edits</strong> keeps changes without generating audio.
          {" "}<strong>{isFinalized ? "Re-finalize" : "Finalize"}</strong> generates audio and makes the stream live.
        </p>

        {/* Snapshots — freeze / restore a good version */}
        <SnapshotPanel
          ref={snapshotPanelRef}
          clientId={clientId}
          base={`${apiBase}/snapshots`}
          status={ptStatus}
          disabled={isGenerating || isFinalizing_}
          onCountChange={(n) => setPtStatus(prev => ({ ...prev, snapshot_count: n }))}
          onRestored={async () => {
            // Restored bundle replaced the working draft server-side. loadAll
            // re-fetches it and resets both saved + local edit tracking, so the
            // restored content shows cleanly with no stale dirty state.
            await loadAll();
          }}
        />

        {/* Generating banner */}
        {isGenerating && (
          <div style={s.generatingBanner}>
            Generating stream — please wait…
          </div>
        )}

        {/* Preview banner + Play preview button */}
        {(isPreviewing || previewAvailable) && (
          <div style={s.previewBanner}>
            {isPreviewing
              ? "Generating preview— please wait…"
              : (
                  <>
                    Preview ready
                    {ptStatus?.preview_scene_ids?.length > 0 &&
                      <> — <strong>{ptStatus.preview_scene_ids.join(", ")}</strong></>}
                    {" "}
                    <button type="button" onClick={() => setShowPreview(true)} style={s.previewLink}>Play preview ▶</button>
                  </>
                )}
          </div>
        )}

        {/* Image picker — portal-style modal, opens per scene */}
        {pickerSceneId && (
          <ImagePicker
            clientId={clientId}
            multi
            excludeIds={(localImages[pickerSceneId] || []).map(img => img.media_id)}
            onSelect={handlePickerSelect}
            onClose={() => setPickerSceneId(null)}
          />
        )}

        {/* Scene cards — locked while a scene is being rewritten */}
        <div style={{ ...s.sceneList, ...(isRewriting ? s.locked : {}) }} aria-busy={isRewriting}>
          {(bundle.scenes || []).map(scene => (
            <SceneCard
              key={scene.scene_id}
              scene={scene}
              narration={localNarrations[scene.scene_id] ?? scene.narration_text ?? ""}
              onChange={val => setLocalNarrations(prev => ({ ...prev, [scene.scene_id]: val }))}
              onScreenText={localOnScreenText[scene.scene_id] ?? scene.on_screen_text ?? ""}
              onOnScreenTextChange={val => setLocalOnScreenText(prev => ({ ...prev, [scene.scene_id]: val }))}
              title={localTitles[scene.scene_id] ?? scene.title ?? ""}
              onTitleChange={val => setLocalTitles(prev => ({ ...prev, [scene.scene_id]: val }))}
              images={localImages[scene.scene_id] ?? scene.pending_images ?? []}
              onImagesChange={imgs => setLocalImages(prev => ({ ...prev, [scene.scene_id]: imgs }))}
              onAddImages={() => setPickerSceneId(scene.scene_id)}
              onPreviewScene={handlePreviewScene}
              isPreviewing={isPreviewing}
              previewSceneId={previewSceneId}
              onRewrite={handleRewrite}
              rewriting={isRewriting && (ptStatus?.rewriting_scene_ids || []).includes(scene.scene_id)}
              rewriteDisabled={isFinalizing_ || isPreviewing || saving}
              nameOf={nameOf}
              textCards={ptStatus?.text_cards}
              beats={localBeats[scene.scene_id]}
              onBeatsChange={bt => setLocalBeats(prev => ({ ...prev, [scene.scene_id]: bt }))}
              clientId={clientId}
              citations={isSection ? (
                <CitationChips citations={ptStatus?.citations?.scenes?.[scene.scene_id]?.citations}
                               focusSection={sectionType} origin={originOf[scene.scene_id]} />
              ) : null}
            />
          ))}
        </div>

        {/* Spacer so last card isn't hidden under sticky bar */}
        <div style={{ height: "var(--space-4)" }} />
      </div>

      {/* ── Sticky bottom action bar ─────────────────────────────────────────
          Layout: [Delete playthrough] ................. [Save edits] [Finalize →]
          Two tiers: destructive text link / secondary ghost / primary filled.
          Leaving the page is handled by "← Back to editor hub" at the top, which
          runs the same dirty-state guard. */}
      <div style={s.stickyBar}>

        {/* Tier 3 — destructive, far left, separated */}
        <button
          style={s.deleteLink}
          onClick={() => setShowDelete(true)}
          disabled={deleting}
        >Delete stream</button>

        {/* Spacer pushes actions to the right */}
        <div style={{ flex: 1 }} />

        {/* Narration pace (Oct 16) is built end to end but hidden while PE has
            pace switched off (Oct 18). PACES and PATCH /playthrough/pace stay;
            to bring it back, restore the select here and set
            NARRATION_PACE_ENABLED on the server. */}

        <button
          style={{ ...s.saveBtn, ...(saving || !isDirty ? s.btnMuted : {}) }}
          onClick={handleSaveDraft}
          disabled={saving || !isDirty}
        >{saving ? "Saving…" : savedFlash ? "Edits saved" : "Save edits"}</button>

        {/* Tier 1 — primary filled */}
        <button
          style={{ ...s.finalizeBtn, ...(isFinalizing_ ? s.btnMuted : {}) }}
          onClick={handleFinalize}
          disabled={isFinalizing_}
        >{finalizeLabel}</button>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = {
  page:    { minHeight: "100dvh", background: "var(--color-bg-subtle)", display: "flex", flexDirection: "column" },
  content: { flex: 1, maxWidth: 900, width: "100%", margin: "0 auto", padding: "var(--space-6) var(--space-6) 0", display: "flex", flexDirection: "column", gap: "var(--space-5)", boxSizing: "border-box", overflowX: "hidden" },
  contentCompact: { padding: "var(--space-4) var(--space-4) 0" },
  header:  { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "var(--space-3)" },
  title:   { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-bold)", color: "var(--color-text-primary)", marginBottom: "var(--space-2)" },
  statusRow:   { display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" },
  subtitle:    { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)" },
  sceneList:   { display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  editedBanner:{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", fontSize: "var(--text-sm)", color: "#92400e" },
  errorBox:    { background: "var(--color-critical-bg)", border: "1px solid var(--color-critical)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)" },
  errorText:   { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  badge:       { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", borderRadius: "var(--radius-full)", padding: "3px 10px" },
  badgeDraft:     { background: "var(--color-bg-inset)",      color: "var(--color-text-secondary)" },
  badgeFinalized: { background: "var(--color-accent-subtle)", color: "var(--color-accent)" },
  badgeEdited:    { background: "rgba(245,158,11,0.12)",      color: "#d97706" },
  badgeFinalizing:{ background: "rgba(245,158,11,0.12)",      color: "#d97706" },
  dirtyHint:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", fontStyle: "italic" },
  empty:       { display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-12)", textAlign: "center" },
  emptyTitle:  { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  hint:        { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  generatingBanner: { padding: "var(--space-3) var(--space-4)", background: "var(--color-accent-subtle)", borderRadius: "var(--radius-md)", fontSize: "var(--text-sm)", color: "var(--color-accent)", fontWeight: "var(--weight-semibold)" },
  previewBanner: { padding: "var(--space-3) var(--space-4)", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: "var(--radius-md)", fontSize: "var(--text-sm)", color: "#065f46", display: "flex", alignItems: "center", gap: "var(--space-3)" },
  previewLink:  { color: "#059669", fontWeight: "var(--weight-semibold)", textDecoration: "none" },
  locked:       { opacity: 0.6, pointerEvents: "none" },
  backLink:    { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", textDecoration: "none" },

  // ── Sticky bar ──────────────────────────────────────────────────────────
  stickyBar: {
    position: "sticky", bottom: 0, zIndex: 10,
    background: "var(--color-surface)",
    borderTop: "1px solid var(--color-border)",
    padding: "var(--space-3) var(--space-6)",
    display: "flex", alignItems: "center", gap: "var(--space-3)",
    maxWidth: "100%",
  },
  // Tier 3 — destructive text link (far left, quiet)
  deleteLink:  { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 },
  // Tier 2 — secondary ghost
  paceLabel:   { display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", marginRight: "var(--space-2)" },
  paceSelect:  { fontSize: "var(--text-sm)", padding: "var(--space-1) var(--space-2)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", background: "var(--color-surface)", color: "var(--color-text-primary)" },
  saveBtn:     { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  // Tier 1 — primary filled
  finalizeBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "#fff", background: "var(--color-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  btnMuted:    { opacity: 0.45, cursor: "not-allowed" },
};

const sc = {
  card:        { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)", minWidth: 0 },
  cardCompact: { padding: "var(--space-4)" },
  // Wraps on a phone: the title takes the first line, the badges and the
  // Finalize button fall to the next, nothing pushes past the edge.
  header:      { display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap", rowGap: "var(--space-2)" },
  titleInput:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-2)", margin: 0, fontFamily: "inherit", flex: "1 1 180px", minWidth: 0, maxWidth: 320 },
  titleInputEdited: { borderColor: "#d97706", background: "rgba(245,158,11,0.03)" },
  sceneId:     { fontSize: 10, color: "var(--color-text-tertiary)", background: "var(--color-bg-inset)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", fontFamily: "monospace" },
  editedBadge: { fontSize: 10, fontWeight: "var(--weight-semibold)", color: "#d97706", background: "rgba(245,158,11,0.1)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", textTransform: "uppercase", letterSpacing: "0.05em" },
  // Images full width above the words (Oct 11): one column, the grid four
  // across on a laptop and two on a phone — the pictures are what the
  // coordinator is judging; the narration reads fine at full width.
  body:        { display: "flex", flexDirection: "column", gap: "var(--space-5)", minWidth: 0 },
  bodyCompact: {},
  contextCol:  { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  narrationCol:{ display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  block:       { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  label:       { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", margin: 0 },
  labelNote:   { fontSize: 10, fontWeight: "normal", textTransform: "none", letterSpacing: 0, color: "var(--color-text-tertiary)" },
  // Three across the column (≈30% each), square, drag handles themselves.
  thumbGrid:   { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "var(--space-2)" },
  thumbGridCompact: { gridTemplateColumns: "repeat(4, minmax(0, 1fr))" },
  thumbWrap:   { position: "relative", aspectRatio: "1 / 1", borderRadius: "var(--radius-md)", overflow: "hidden", background: "var(--color-bg-inset)", cursor: "grab", outline: "2px solid transparent", outlineOffset: 2, transition: "transform 120ms ease, opacity 120ms ease, outline-color 120ms ease" },
  thumb:       { width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" },
  thumbDragging: { opacity: 0.4, cursor: "grabbing" },
  thumbOverBefore: { outlineColor: "var(--color-accent)", transform: "translateX(6px)" },
  thumbOverAfter:  { outlineColor: "var(--color-accent)", transform: "translateX(-6px)" },
  thumbIndex:  { position: "absolute", left: 6, bottom: 6, minWidth: 20, height: 20, padding: "0 6px", borderRadius: 999, background: "rgba(26,25,21,0.7)", color: "#fff", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center" },
  thumbWhy:    { position: "absolute", left: 30, right: 4, bottom: 6, height: 20, padding: "0 6px", borderRadius: 999, background: "rgba(26,25,21,0.7)", color: "#fff", fontSize: 10, lineHeight: "20px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  thumbRemove: { position: "absolute", top: 4, right: 4, width: 24, height: 24, borderRadius: 999, border: "none", background: "rgba(26,25,21,0.7)", color: "#fff", fontSize: 15, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
  imgLabelRow: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  addImgBtn:   { fontSize: 10, fontWeight: "var(--weight-semibold)", color: "var(--color-accent)", background: "none", border: "1px solid var(--color-accent)", borderRadius: "var(--radius-sm)", padding: "2px 8px", cursor: "pointer" },
  noImgs:      { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", fontStyle: "italic", margin: 0 },
  reauthorBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  modeSelect: { fontSize: 11, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", background: "var(--color-bg-base)", color: "var(--color-text-secondary)", padding: "2px 6px" },
  rewriteBtn:  { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "var(--color-accent)", background: "none", border: "1px solid var(--color-accent)", borderRadius: "var(--radius-sm)", padding: "2px 8px", cursor: "pointer" },
  previewBtn:  { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "#059669", background: "none", border: "1px solid #059669", borderRadius: "var(--radius-sm)", padding: "2px 8px", cursor: "pointer" },
  textarea:    { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", fontFamily: "inherit", resize: "vertical", width: "100%", lineHeight: "var(--leading-normal)", boxSizing: "border-box" },
  textareaEdited: { borderColor: "#d97706", background: "rgba(245,158,11,0.03)" },
  onScreenInput: { fontSize: "var(--text-xs)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-secondary)", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
};

const m = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 },
  box:     { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-6) var(--space-8)", width: 400, maxWidth: "calc(100vw - 48px)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  title:   { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  msg:     { fontSize: "var(--text-base)", color: "var(--color-text-primary)", margin: 0 },
  body:    { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: 0 },
  warn:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", lineHeight: "var(--leading-normal)", margin: 0, fontWeight: "var(--weight-medium)" },
  label:   { display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  input:   { marginTop: "var(--space-1)", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", width: "100%", boxSizing: "border-box" },
  row:     { display: "flex", gap: "var(--space-2)", flexWrap: "wrap" },
  leaveBtn:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-text-primary)", color: "var(--color-bg-base)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  keepBtn:   { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: "var(--space-2) var(--space-3)" },
  deleteBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-critical)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  disabled:  { opacity: 0.4, cursor: "not-allowed" },
  saveGatePrimary:   { width: "100%", fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-5)", cursor: "pointer" },
  saveGateSecondary: { width: "100%", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "none", color: "var(--color-text-primary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-5)", cursor: "pointer" },
  saveGateGhost:     { width: "100%", fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: "var(--space-2)" },
};
